// Lightweight Web Audio celebration chime (Feb 26 2026).
//
// Two soft sine notes ~130ms apart — think "ding-ding" — with a gentle
// exponential decay so it lands like an iOS system-y confirmation, never
// alarming. No asset download, no third-party dep, ~200 bytes payload.
//
// Usage:
//   import { playCelebrationChime } from '@/lib/celebrationChime';
//   playCelebrationChime('match');            // brighter major-third chord
//   playCelebrationChime('meet');             // warm perfect-fifth
//   playCelebrationChime('keep');             // low + soft
//   playCelebrationChime('met');              // neutral confirm
//
// Respects prefers-reduced-motion → skips playback so accessibility settings
// carry over from motion to sound.

const NOTE_MAP = {
  // (freq1 Hz, freq2 Hz, gain 0-1)
  match:   [660, 880, 0.14],   // E5 → A5 — bright, celebratory
  meet:    [523, 784, 0.14],   // C5 → G5 — friendly, warm
  planned: [587, 880, 0.13],   // D5 → A5 — anticipation, forward motion
  met:     [587, 740, 0.11],   // D5 → F#5 — subtle confirm
  keep:    [440, 660, 0.12],   // A4 → E5 — soft, romantic
};

let _ctx = null;
function _getCtx() {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { _ctx = new AC(); } catch { return null; }
  return _ctx;
}

export function playCelebrationChime(kind = 'match') {
  // Skip if user prefers reduced motion — same accessibility contract as animations.
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  const ctx = _getCtx();
  if (!ctx) return;
  // AudioContext can start "suspended" if there's been no user gesture yet;
  // resume() is safe to call unconditionally.
  if (ctx.state === 'suspended') {
    try { ctx.resume(); } catch { /* non-fatal */ }
  }
  const [f1, f2, gain] = NOTE_MAP[kind] || NOTE_MAP.match;
  const now = ctx.currentTime;
  _tone(ctx, f1, gain, now,        0.42);   // first note (420ms)
  _tone(ctx, f2, gain, now + 0.13, 0.55);   // second note, 130ms later (550ms)
}

function _tone(ctx, freq, gain, startAt, dur) {
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startAt);
    // Quick attack, gentle decay so it feels warm not tinny.
    g.gain.setValueAtTime(0.0001, startAt);
    g.gain.exponentialRampToValueAtTime(gain, startAt + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + dur + 0.02);
  } catch { /* non-fatal */ }
}
